export class Visualizer {
    constructor(canvasId) {
        this.canvas = document.getElementById(canvasId);
        this.ctx = this.canvas.getContext('2d');
        
        this.resizeCanvas();
        window.addEventListener('resize', () => this.resizeCanvas());
        
        this.particles = [];
    }

    resizeCanvas() {
        this.canvas.width = this.canvas.offsetWidth;
        this.canvas.height = this.canvas.offsetHeight;
    }

    clear() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    /**
     * Update visualization with current pitch and target
     */
    update(frequency, currentNote, targetNote) {
        this.clear();
        
        if (targetNote) {
            this.drawPitchComparison(frequency, currentNote, targetNote);
        } else {
            this.drawPitchIndicator(frequency, currentNote);
        }
    }

    /**
     * Draw waveform visualization
     */
    updateWaveform(buffer) {
        this.clear();
        
        this.ctx.strokeStyle = '#667eea';
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        
        const sliceWidth = this.canvas.width / buffer.length;
        let x = 0;
        
        for (let i = 0; i < buffer.length; i++) {
            const v = buffer[i];
            const y = (v + 1) / 2 * this.canvas.height;
            
            if (i === 0) {
                this.ctx.moveTo(x, y);
            } else {
                this.ctx.lineTo(x, y);
            }
            
            x += sliceWidth;
        }
        
        this.ctx.stroke();
    }

    /**
     * Draw pitch indicator without target
     */
    drawPitchIndicator(frequency, note) {
        const centerX = this.canvas.width / 2;
        const centerY = this.canvas.height / 2;
        
        // Map frequency to position (80 Hz to 1000 Hz range)
        const minFreq = 80;
        const maxFreq = 1000;
        const normalizedFreq = (Math.log(frequency) - Math.log(minFreq)) / 
                               (Math.log(maxFreq) - Math.log(minFreq));
        const x = normalizedFreq * this.canvas.width;
        
        // Draw frequency line
        this.ctx.strokeStyle = '#667eea';
        this.ctx.lineWidth = 3;
        this.ctx.beginPath();
        this.ctx.moveTo(x, 0);
        this.ctx.lineTo(x, this.canvas.height);
        this.ctx.stroke();
        
        // Draw circle at pitch
        this.ctx.fillStyle = '#764ba2';
        this.ctx.beginPath();
        this.ctx.arc(x, centerY, 20, 0, 2 * Math.PI);
        this.ctx.fill();
        
        // Draw note name
        this.ctx.fillStyle = '#333';
        this.ctx.font = 'bold 24px Arial';
        this.ctx.textAlign = 'center';
        this.ctx.fillText(note, x, centerY - 40);
    }

    /**
     * Draw pitch comparison with target
     */
    drawPitchComparison(frequency, currentNote, targetNote) {
        const centerY = this.canvas.height / 2;
        const centerX = this.canvas.width / 2;
        
        // Calculate target frequency
        // We need to import MusicTheory - for now use simplified calculation
        const A4 = 440;
        const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        
        const match = targetNote.match(/^([A-G]#?)(\d+)$/);
        if (!match) return;
        
        const [, note, octaveStr] = match;
        const octave = parseInt(octaveStr);
        const noteIndex = notes.indexOf(note);
        const midiNote = (octave + 1) * 12 + noteIndex;
        const targetFreq = A4 * Math.pow(2, (midiNote - 69) / 12);
        
        if (!targetFreq) return;
        
        // Draw target line
        this.ctx.strokeStyle = '#fbbf24';
        this.ctx.lineWidth = 4;
        this.ctx.setLineDash([10, 5]);
        this.ctx.beginPath();
        this.ctx.moveTo(centerX, 0);
        this.ctx.lineTo(centerX, this.canvas.height);
        this.ctx.stroke();
        this.ctx.setLineDash([]);
        
        // Draw target note name
        this.ctx.fillStyle = '#f59e0b';
        this.ctx.font = 'bold 20px Arial';
        this.ctx.textAlign = 'center';
        this.ctx.fillText(`Target: ${targetNote}`, centerX, 30);
        
        // Calculate cents difference
        const cents = 1200 * Math.log2(frequency / targetFreq);
        
        if (cents !== null && !isNaN(cents)) {
            // Map cents to position (-50 to +50 cents)
            const maxCents = 50;
            const normalizedCents = Math.max(-1, Math.min(1, cents / maxCents));
            const x = centerX + normalizedCents * (this.canvas.width / 4);
            
            // Color based on accuracy
            let color;
            if (Math.abs(cents) < 10) {
                color = '#10b981'; // Green - perfect
            } else if (Math.abs(cents) < 25) {
                color = '#f59e0b'; // Yellow - close
            } else {
                color = '#ef4444'; // Red - off
            }
            
            // Draw current pitch indicator
            this.ctx.fillStyle = color;
            this.ctx.beginPath();
            this.ctx.arc(x, centerY, 25, 0, 2 * Math.PI);
            this.ctx.fill();
            
            // Draw current note
            this.ctx.fillStyle = '#fff';
            this.ctx.font = 'bold 16px Arial';
            this.ctx.textAlign = 'center';
            this.ctx.fillText(currentNote, x, centerY + 5);
            
            // Draw cents indicator
            this.ctx.fillStyle = '#333';
            this.ctx.font = '14px Arial';
            const centsText = cents > 0 ? `+${cents.toFixed(0)}¢` : `${cents.toFixed(0)}¢`;
            this.ctx.fillText(centsText, x, centerY + 50);
            
            // Draw arrow indicating direction
            if (Math.abs(cents) > 10) {
                this.drawArrow(x, centerY - 40, cents > 0 ? 'left' : 'right', color);
            }
        }
    }

    /**
     * Draw directional arrow
     */
    drawArrow(x, y, direction, color) {
        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        
        const arrowSize = 15;
        
        if (direction === 'left') {
            this.ctx.moveTo(x, y);
            this.ctx.lineTo(x + arrowSize, y - arrowSize / 2);
            this.ctx.lineTo(x + arrowSize, y + arrowSize / 2);
        } else {
            this.ctx.moveTo(x, y);
            this.ctx.lineTo(x - arrowSize, y - arrowSize / 2);
            this.ctx.lineTo(x - arrowSize, y + arrowSize / 2);
        }
        
        this.ctx.closePath();
        this.ctx.fill();
    }

    /**
     * Celebration animation
     */
    celebrate() {
        // Create particles
        for (let i = 0; i < 50; i++) {
            this.particles.push({
                x: this.canvas.width / 2,
                y: this.canvas.height / 2,
                vx: (Math.random() - 0.5) * 10,
                vy: (Math.random() - 0.5) * 10,
                color: this.randomColor(),
                size: Math.random() * 5 + 2,
                life: 1
            });
        }
        
        this.animateParticles();
    }

    animateParticles() {
        if (this.particles.length === 0) return;
        
        this.clear();
        
        // Update and draw particles
        this.particles = this.particles.filter(p => {
            p.x += p.vx;
            p.y += p.vy;
            p.vy += 0.2; // Gravity
            p.life -= 0.02;
            
            if (p.life > 0) {
                this.ctx.fillStyle = p.color;
                this.ctx.globalAlpha = p.life;
                this.ctx.beginPath();
                this.ctx.arc(p.x, p.y, p.size, 0, 2 * Math.PI);
                this.ctx.fill();
                return true;
            }
            return false;
        });
        
        this.ctx.globalAlpha = 1;
        
        if (this.particles.length > 0) {
            requestAnimationFrame(() => this.animateParticles());
        }
    }

    randomColor() {
        const colors = ['#667eea', '#764ba2', '#fbbf24', '#f59e0b', '#10b981', '#ef4444'];
        return colors[Math.floor(Math.random() * colors.length)];
    }
}
